import * as React from "react";

import { Card } from "../card";
import { TableCell, TableRow } from "../table";
import { cn } from "../utils";
import { TextureOverlay } from "./grain-overlay";

export interface EmptyStateProps {
  /** Lucide icon (or any node) shown above the title. */
  icon?: React.ReactNode;
  /** Heading — one short line. text-foreground. */
  title: React.ReactNode;
  /** Optional one-line subtext in muted-foreground. */
  description?: React.ReactNode;
  /** Optional action button or link. */
  action?: React.ReactNode;
  /**
   * `card`   — full Card with centered content (default). Use as the body
   *            of an empty page.
   * `row`    — table-row shaped node; pass to `<DataTable empty={...}>`.
   * `inline` — compact, no Card. Use inside an already-Carded panel.
   */
  variant?: "card" | "row" | "inline";
  /** Required for `row` variant — column span of the parent table. */
  colSpan?: number;
  className?: string;
}

/**
 * The standard empty-state block. Replaces ad-hoc empty strings and
 * hand-rolled empty divs across every product. Tone rule: tell the user
 * what to do next, not what's missing.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  variant = "card",
  colSpan,
  className,
}: EmptyStateProps) {
  const body = (
    <div
      data-slot="empty-state-body"
      className={cn(
        "flex flex-col items-center justify-center gap-2 text-center",
        variant === "card" && "py-12 px-6",
        variant === "inline" && "py-8 px-4",
        // A table's blank state should read as an intentional panel, not a
        // collapsed strip — give the row variant a comfortable minimum height so
        // the empty Card has presence and the centered guidance/CTA has room.
        variant === "row" && "min-h-52 py-12 px-6",
      )}
    >
      {icon && (
        <div className="text-muted-foreground" aria-hidden>
          {icon}
        </div>
      )}
      <div className="type-body text-foreground">{title}</div>
      {description && (
        <div className="max-w-md type-body text-muted-foreground">
          {description}
        </div>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );

  if (variant === "row") {
    return (
      <TableRow data-slot="empty-state-row">
        {/* TableCell defaults to whitespace-nowrap (good for data columns);
            reset it here so the multi-line description wraps instead of being
            clipped by the card edge. */}
        <TableCell
          colSpan={colSpan ?? 1}
          className={cn("p-0 whitespace-normal", className)}
        >
          {body}
        </TableCell>
      </TableRow>
    );
  }

  if (variant === "inline") {
    return (
      <div
        data-slot="empty-state-inline"
        className={cn("w-full", className)}
      >
        {body}
      </div>
    );
  }

  return (
    <Card
      data-slot="empty-state-card"
      className={cn("relative isolate overflow-hidden p-0", className)}
    >
      {/* A faint diagonal hatch pooled in the bottom-right corner gives the
          otherwise-blank panel some surface. Behind the centered body. */}
      <TextureOverlay variant="lines" />
      <div className="relative z-10">{body}</div>
    </Card>
  );
}
